import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import {
  getOptimizationsQueryData,
  optimizationsQuery,
  setOptimizationsQueryData,
} from "#modules/optimizations/infra/optimizations.query";
import { withPreservedCompatibleLines } from "#modules/optimizations/infra/preserveCompatibleLines";
import { updateMachineTimes } from "#modules/optimizations/infra/updateMachineTimes.api";
import { queryKeys } from "#shared/queryKeys/queryKeys";

export const useUpdateMachineTimes = ({ quoteRequestId }: { quoteRequestId: string }) => {
  const queryClient = useQueryClient();
  const { queryKey } = optimizationsQuery({ quoteRequestId });

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: updateMachineTimes,
    onMutate: async ({ optimizationId, machinePreparationTime, otherMachineTime }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousMetadata = getOptimizationsQueryData(queryClient, quoteRequestId)?.find(
        (optimization) => optimization.id === optimizationId,
      )?.machineTimesOptimizationMetadata;

      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === optimizationId && optimization.machineTimesOptimizationMetadata
            ? {
                ...optimization,
                machineTimesOptimizationMetadata: {
                  ...optimization.machineTimesOptimizationMetadata,
                  machinePreparationTime,
                  otherMachineTime,
                },
              }
            : optimization,
        ),
      );

      return { previousMetadata };
    },
    onError: (err: Error, { optimizationId }, context) => {
      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === optimizationId &&
          optimization.machineTimesOptimizationMetadata &&
          context?.previousMetadata
            ? { ...optimization, machineTimesOptimizationMetadata: context.previousMetadata }
            : optimization,
        ),
      );

      showErrorToast({ title: err.message });
      errorHandler(err);
    },
    onSuccess: (updatedOptimization) => {
      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === updatedOptimization.id
            ? withPreservedCompatibleLines(optimization, updatedOptimization)
            : optimization,
        ),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.optimizations.enabledByQuoteRequest(quoteRequestId),
      });
    },
  });

  return { updateMachineTimes: mutate, updateMachineTimesAsync: mutateAsync, ...rest };
};
